import { useQuery } from "@tanstack/react-query";

import { bffAxios } from "@av/config/api-config/axios-config";
import { adminSettingsProductPaths } from "./useAdminSettingsAccess";
import { platformQueryKeys } from "../../../services/platform-query-key-factory";

import type { ProductCode } from "../../../services/admin-settings/type";

type CompanyUser = {
  id: string;
  displayName: string;
  products: string[];
};

type CollectionResponse<T> = {
  itemCount?: number;
  items: T[];
};

type Role = {
  code: string;
};

type SecurityGroup = {
  id: string;
  name: string;
};

export type ProductDetails = {
  roleCount: number;
  securityGroupCount: number;
  userCount: number;
};

const productCodeToCompanyUserProduct: Record<ProductCode, string> = {
  ats: "ATS",
  hr: "HR",
};

function useFetchProductDetails({
  enabled,
  productCode,
}: {
  enabled: boolean;
  productCode: ProductCode;
}) {
  return useQuery({
    enabled,
    queryFn: async (): Promise<ProductDetails> => {
      const [companyUsersResponse, securityGroupsResponse, rolesResponse] = await Promise.all([
        bffAxios.get<CompanyUser[]>("/company/users"),
        bffAxios.get<CollectionResponse<SecurityGroup>>(`/company/security-groups/${productCode}`),
        bffAxios.get<CollectionResponse<Role>>(`/company/roles/${productCode}`),
      ]);

      const roles = rolesResponse.data.items.filter(
        (role) => !(role.code === "EVENTBRIDGE_SERVICE_ACCOUNT")
      );

      return {
        roleCount: roles.length,
        securityGroupCount:
          securityGroupsResponse.data.itemCount ?? securityGroupsResponse.data.items.length,
        userCount: companyUsersResponse.data.filter((user) =>
          user.products.includes(productCodeToCompanyUserProduct[productCode])
        ).length,
      };
    },
    queryKey: platformQueryKeys.adminSettingsProductDetails(productCode),
  });
}

export function useFetchAdminSettingsProductDetails({
  enabled,
  productPermissions,
}: {
  enabled: boolean;
  productPermissions: Record<ProductCode, boolean>;
}): Record<string, ProductDetails | undefined> {
  const { data: hrProductDetails } = useFetchProductDetails({
    enabled: enabled && productPermissions.hr,
    productCode: "hr",
  });

  const { data: atsProductDetails } = useFetchProductDetails({
    enabled: enabled && productPermissions.ats,
    productCode: "ats",
  });

  return {
    [adminSettingsProductPaths.ats]: atsProductDetails,
    [adminSettingsProductPaths.hr]: hrProductDetails,
    // TODO: Wire Payroll product details when the Payroll counts endpoint is available.
    [adminSettingsProductPaths.payroll]: undefined,
  };
}
